import { useMemo, useState } from 'react';
import { Modal, Input, theme, ColorPicker, Button, Typography } from 'antd';
import type { Color } from 'antd/es/color-picker';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormStyles } from '@/styles/formStyles';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useCategoryMutations } from '@/hooks/useCategories';
import { categoryFormModalSchema, type CategoryFormModalData } from '@/schemas/forms';
import type { Category } from '@/models/category';
import type { CategoryListItem } from '@/types/api/category';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { CATEGORY_COLOR_PRESETS, DEFAULT_CATEGORY_COLOR } from '@/constants/categoryColorPresets';

interface CategoryFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (updatedCategory?: CategoryListItem) => void | Promise<void>;
  category?: Category | CategoryListItem | null;
}

// Default values for empty/create mode
const defaultCategoryValues: CategoryFormModalData = {
  name: '',
  color: DEFAULT_CATEGORY_COLOR,
  notes: '',
};

// Type guard to check if category is CategoryListItem
const isCategoryListItem = (cat: Category | CategoryListItem): cat is CategoryListItem => {
  return 'categoryId' in cat && 'category' in cat;
};

const CategoryFormModal = ({ visible, onClose, onSuccess, category }: CategoryFormModalProps) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useVendorTranslation();
  const { handleError, handleSuccess } = useErrorHandler();
  const { createCategory, updateCategory } = useCategoryMutations();
  const [loading, setLoading] = useState(false);

  const isEditMode = !!category;

  // Compute form values using useMemo (prevents unnecessary recalculations)
  const formValues = useMemo(() => {
    if (!visible) {
      return defaultCategoryValues; // Reset to defaults when modal closes
    }
    if (!category) {
      return defaultCategoryValues; // Create mode
    }
    // Edit mode - handle both CategoryListItem and Category formats
    const isListItem = isCategoryListItem(category);

    // Get notes/description - handle both old (description) and new (notes) field names
    let notesValue = '';
    if (isListItem) {
      // CategoryListItem may have either 'description' or 'notes' depending on API version
      notesValue = (category as CategoryListItem & { notes?: string }).notes || category.description || '';
    } else {
      notesValue = category.notes || '';
    }

    return {
      name: isListItem ? category.category : category.name,
      color: category.color || DEFAULT_CATEGORY_COLOR,
      notes: notesValue,
    };
  }, [visible, category]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<CategoryFormModalData>({
    resolver: zodResolver(categoryFormModalSchema),
    defaultValues: defaultCategoryValues,
    // Use `values` prop for reactive updates (recommended by RHF docs)
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: CategoryFormModalData) => {
    // Prevent duplicate submissions
    if (loading) return;

    try {
      setLoading(true);

      // Transform frontend data to match API schema
      const apiPayload = {
        category: data.name, // API expects 'category' not 'name'
        typeIds: [1], // Default to vendor type
        color: data.color || DEFAULT_CATEGORY_COLOR, // Default to primary purple
        description: data.notes || '', // API expects 'description' not 'notes'
      };

      let updatedCategory: CategoryListItem | undefined;

      if (isEditMode && category) {
        // Handle both CategoryListItem (categoryId) and Category (id) formats
        const isListItem = isCategoryListItem(category);
        const categoryId = isListItem ? category.categoryId : parseInt(category.id);

        // Use mutation hook for cache invalidation
        const response = await updateCategory(categoryId, apiPayload);
        // Transform response to CategoryListItem format for immediate UI update
        updatedCategory = {
          categoryId,
          category: response.category,
          typeInfo: response.typeInfo || [],
          description: response.description || '',
          color: response.color || DEFAULT_CATEGORY_COLOR,
          isActive: response.isActive ?? true,
          createdAt: response.createdAt,
          updatedAt: response.updatedAt,
        };
        handleSuccess(t('vendor.messages.categoryUpdateSuccess'));
      } else {
        // Use mutation hook for cache invalidation
        await createCategory(apiPayload);
        handleSuccess(t('vendor.messages.categoryCreateSuccess'));
      }

      reset();
      // Pass updated data to parent for immediate UI update, await if async
      await onSuccess(updatedCategory);
      onClose();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={isEditMode ? t('vendor.categories.form.editTitle') : t('vendor.categories.form.createTitle')}
      footer={null}
      width={450}
    >
      <style>
        {`
          .category-form-modal .ant-select-selector {
            height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
          .category-form-modal .ant-color-picker-trigger {
            height: 44px !important;
            display: flex !important;
            align-items: center !important;
          }
          .category-form-modal .ant-color-picker-trigger .ant-color-picker-color-block {
            display: flex !important;
            align-items: center !important;
          }
          .category-form-modal .ant-color-picker-trigger .ant-color-picker-trigger-text {
            display: flex !important;
            align-items: center !important;
            line-height: 44px !important;
          }
        `}
      </style>
      <div style={{ maxWidth: '400px', margin: '0 auto' }}>
        <form
          id="category-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(onSubmit)(e);
          }}
          className="category-form-modal"
        >
        {/* Category Name */}
        <div
          style={{
            marginBottom: '12px',
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <Typography.Text
            strong
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {t('vendor.fields.categoryName')}
          </Typography.Text>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  size="large"
                  placeholder={t('vendor.categories.form.namePlaceholder')}
                  style={{
                    ...formStyles.inputStyle,
                    borderColor: errors.name && (touchedFields.name || isSubmitted) ? token.colorError : undefined,
                  }}
                />
                {errors.name && (touchedFields.name || isSubmitted) && (
                  <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                    {t(errors.name?.message ?? '')}
                  </Typography.Text>
                )}
              </>
            )}
          />
        </div>

        {/* Color */}
        <div
          style={{
            marginBottom: '12px',
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <Typography.Text
            strong
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {t('vendor.fields.color')}
          </Typography.Text>
          <Controller
            name="color"
            control={control}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                value={value}
                onChange={(color: Color) => {
                  const hexColor = typeof color === 'string' ? color : color?.toHexString();
                  onChange(hexColor);
                }}
                showText
                presets={[
                  {
                    label: t('vendor.categories.form.recommendedColors'),
                    colors: [...CATEGORY_COLOR_PRESETS],
                  },
                ]}
              />
            )}
          />
        </div>

        {/* Notes */}
        <div
          style={{
            marginBottom: '12px',
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <Typography.Text
            strong
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            {t('vendor.fields.notes')}
          </Typography.Text>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                size="large"
                rows={4}
                placeholder={t('vendor.categories.form.notesPlaceholder')}
                style={{ resize: 'none', borderRadius: radiusTokens.card }}
              />
            )}
          />
        </div>
      </form>

      {/* Footer Actions */}
      <div
        className="tw-flex tw-justify-end tw-gap-2"
        style={{
          paddingTop: '12px',
        }}
      >
        <Button
          onClick={onClose}
          style={{ height: `${controlHeightTokens.controlHeightXL}px`, fontSize: token.fontSizeSM }}
        >
          {t('vendor.actions.cancel')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          form="category-form"
          loading={loading}
          style={{ height: `${controlHeightTokens.controlHeightXL}px`, fontSize: token.fontSizeSM }}
        >
          {t('vendor.actions.confirm')}
        </Button>
      </div>
      </div>
    </Modal>
  );
};

export default CategoryFormModal;
